import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerNamesByLastName } from "../utils/swimmerSort";
import MascotLogo from "./MascotLogo";

const SESSION_USER_KEY = "fantasySwimSessionUser";
const readMyMascot = () => {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.profileImageDataUrl || "";
  } catch {
    return "";
  }
};

const API = API_BASE_URL;
const WATCH_KEY = "fantasySwimWatchGroup";
const PENDING_TRADES_KEY = "fantasyPendingTrades";

const STROKE_ABBR = {
  Freestyle: "FR",
  Backstroke: "BK",
  Breaststroke: "BR",
  Butterfly: "FLY",
  "Individual Medley": "IM",
};

const STROKE_ORDER = [
  "Freestyle",
  "Backstroke",
  "Breaststroke",
  "Butterfly",
  "Individual Medley",
];

const TEAM_SLOT_ROWS = [
  { label: "6 & Under", girlKey: "6u_F", boyKey: "6u_M" },
  { label: "7-8", girlKey: "7-8_F", boyKey: "7-8_M" },
  { label: "9-10", girlKey: "9-10_F", boyKey: "9-10_M" },
  { label: "11-12", girlKey: "11-12_F", boyKey: "11-12_M" },
  { label: "13-14", girlKey: "13-14_F", boyKey: "13-14_M" },
];

const formatTime = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const seconds = Number(value);
  const minutes = Math.floor(seconds / 60);
  const remainder = (seconds % 60).toFixed(2).padStart(5, "0");
  return minutes > 0 ? `${minutes}:${remainder}` : remainder;
};

const buildPointTrend = (results = []) => {
  const sorted = [...results].sort((a, b) => a.sequence - b.sequence);
  let running = 0;
  return sorted.map((row) => {
    running += Number(row.fantasy_points || 0);
    return running;
  });
};

const renderTimesSummary = (bestTimes = {}) => {
  const parts = STROKE_ORDER.map((stroke) => {
    const time = bestTimes[stroke];
    if (time == null) return null;
    return `${STROKE_ABBR[stroke]} ${formatTime(time)}`;
  }).filter(Boolean);

  return parts.length ? parts.join(" • ") : "No recorded times";
};

const getPotential = (swimmer) => {
  const results = swimmer.results || [];
  const drops = results
    .map((row) => Number(row.time_drop))
    .filter((value) => !Number.isNaN(value));
  const avgDrop =
    drops.length > 0
      ? drops.reduce((sum, value) => sum + value, 0) / drops.length
      : 0;
  const trend = buildPointTrend(results);
  const trendGain = trend.length > 1 ? trend[trend.length - 1] - trend[0] : 0;
  const score = avgDrop * 15 + trendGain / 3;

  let label = "Watch";
  if (score >= 8) label = "High";
  else if (score >= 3) label = "Medium";

  return {
    label,
    avgDrop,
    trendGain,
  };
};

const buildMeetBreakdownFromResults = (results = []) => {
  const meetsById = new Map();

  results.forEach((row, index) => {
    if (!row?.is_active) return;

    const rawMeetId = (row.meet_id || "").trim();
    const meetId = rawMeetId || `meet-${index}`;
    const meetLabel = (row.meet_label || rawMeetId || "Meet").trim();
    const swimmerName = (row.name || "").trim();
    const points = Number(row.scoring_points || 0);

    if (!meetsById.has(meetId)) {
      meetsById.set(meetId, {
        meetId,
        meetLabel,
        totalPoints: 0,
        swimmersByName: new Map(),
        order: index,
      });
    }

    const meetEntry = meetsById.get(meetId);
    meetEntry.totalPoints += points;

    if (swimmerName) {
      const current = Number(meetEntry.swimmersByName.get(swimmerName) || 0);
      meetEntry.swimmersByName.set(swimmerName, current + points);
    }
  });

  return [...meetsById.values()]
    .sort((a, b) => a.order - b.order)
    .map((meet) => ({
      meetId: meet.meetId,
      meetLabel: meet.meetLabel,
      totalPoints: Number(meet.totalPoints || 0),
      swimmers: [...meet.swimmersByName.entries()]
        .map(([name, points]) => ({
          name,
          points: Number(points || 0),
        }))
        .sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          return a.name.localeCompare(b.name);
        }),
    }));
};

const getCoachTotalFromLeaderboard = (leaderboard = [], coachName = "") => {
  const normalizedCoach = (coachName || "").trim().toLowerCase();
  if (!normalizedCoach) return null;

  const row = (leaderboard || []).find(
    (item) => (item?.coach || "").trim().toLowerCase() === normalizedCoach,
  );

  if (!row) return null;
  return Number(row.total_points || 0);
};

function HomeDashboard({ coachName }) {
  const navigate = useNavigate();
  const [allSwimmers, setAllSwimmers] = useState([]);
  const [lineupSlots, setLineupSlots] = useState([]);
  const [coachScoreTotal, setCoachScoreTotal] = useState(0);
  const [meetBreakdown, setMeetBreakdown] = useState([]);
  const [showMeetBreakdown, setShowMeetBreakdown] = useState(false);
  const [selectedMeet, setSelectedMeet] = useState(null);
  const [watchSet, setWatchSet] = useState(new Set());
  const [pendingTrades, setPendingTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCaptainModal, setShowCaptainModal] = useState(false);
  const [myMatchup, setMyMatchup] = useState(null);
  const [matchupWeek, setMatchupWeek] = useState(null);
  const [mySwissRecord, setMySwissRecord] = useState(null);
  const [mascots, setMascots] = useState({});

  const mascotFor = (name) => {
    const key = (name || "").toLowerCase();
    return mascots[key]?.image || "";
  };

  useEffect(() => {
    const token = getAccessToken();

    if (!token) {
      setAllSwimmers([]);
      setLineupSlots([]);
      setCoachScoreTotal(0);
      setMeetBreakdown([]);
      setLoading(false);
      setError("You must be logged in to view home analytics.");
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    Promise.allSettled([
      axios.get(`${API}/tracker/swimmers`, { headers }),
      axios.get(`${API}/team/lineup`, { headers }),
      axios.get(`${API}/my-results`, { headers }),
      axios.get(`${API}/coach-leaderboard`, { headers }),
      axios.get(`${API}/draft/state`, { headers }),
    ]).then(
      ([swimmersResult, lineupResult, myResultsResult, leaderboardResult, draftResult]) => {
        if (swimmersResult.status === "fulfilled") {
          setAllSwimmers(swimmersResult.value.data.swimmers || []);
          setError("");
        } else {
          const reason = swimmersResult.reason;
          console.error(reason);
          setAllSwimmers([]);
          if (reason?.response?.status === 401) {
            localStorage.removeItem("fantasySwimAccessToken");
            localStorage.removeItem("fantasySwimSessionUser");
            setError(
              "Your session has expired or is invalid. Please log out and log back in.",
            );
          } else {
            setError(
              "Unable to load swimmer analytics. Is the backend running?",
            );
          }
        }

        if (lineupResult.status === "fulfilled") {
          const lineupData = lineupResult.value.data;
          setLineupSlots(lineupData?.active_slots || []);
          const captains = lineupData?.captains || {};
          const requiredAges = ["6u", "7-8", "9-10"];
          const hasAllCaptains = requiredAges.every((age) => captains[age]);
          const draftCompleted = draftResult.status === "fulfilled" && draftResult.value.data?.completed;
          if (!hasAllCaptains && draftCompleted) {
            setShowCaptainModal(true);
          }
        } else {
          setLineupSlots([]);
        }

        let nextMeetBreakdown = [];
        if (myResultsResult.status === "fulfilled") {
          const myResults = Array.isArray(myResultsResult.value.data?.results)
            ? myResultsResult.value.data.results
            : [];
          nextMeetBreakdown = buildMeetBreakdownFromResults(myResults);
        }
        setMeetBreakdown(nextMeetBreakdown);

        let nextScoreTotal = nextMeetBreakdown.reduce(
          (sum, meet) => sum + Number(meet.totalPoints || 0),
          0,
        );

        if (leaderboardResult.status === "fulfilled") {
          const leaderboard = Array.isArray(
            leaderboardResult.value.data?.leaderboard,
          )
            ? leaderboardResult.value.data.leaderboard
            : [];
          const leaderboardTotal = getCoachTotalFromLeaderboard(
            leaderboard,
            coachName,
          );
          if (leaderboardTotal != null) {
            nextScoreTotal = leaderboardTotal;
          }
        }

        setCoachScoreTotal(nextScoreTotal);

        setLoading(false);
      },
    );

    axios.get(`${API}/matchups`).then((res) => {
      const matchups = res.data?.matchups || [];
      const week = res.data?.current_week ?? null;
      const generated = res.data?.generated ?? false;
      const swissRecords = res.data?.swiss_records || {};
      if (!generated) return;
      const found = matchups.find(
        (pair) =>
          pair[0]?.coach?.toLowerCase() === coachName.toLowerCase() ||
          pair[1]?.coach?.toLowerCase() === coachName.toLowerCase(),
      );
      if (found) {
        const opponent =
          found[0]?.coach?.toLowerCase() === coachName.toLowerCase()
            ? found[1]
            : found[0];
        setMyMatchup(opponent);
        setMatchupWeek(week);
      }
      const myRec = swissRecords[coachName] || null;
      if (myRec) setMySwissRecord(myRec);
    }).catch(() => {});

    axios.get(`${API}/coaches/mascots`).then((res) => {
      setMascots(res.data?.mascots || {});
    }).catch(() => {});
  }, [coachName]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WATCH_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        setWatchSet(
          new Set(parsed.filter((value) => typeof value === "string")),
        );
      }
    } catch {
      setWatchSet(new Set());
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_TRADES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        setPendingTrades(parsed);
      }
    } catch {
      setPendingTrades([]);
    }
  }, []);

  const sortedSwimmers = useMemo(() => {
    return [...allSwimmers].sort((a, b) =>
      compareSwimmerNamesByLastName(a?.name, b?.name),
    );
  }, [allSwimmers]);

  const watchedSwimmers = useMemo(() => {
    return sortedSwimmers.filter((swimmer) => watchSet.has(swimmer.name));
  }, [sortedSwimmers, watchSet]);

  const myTeamSwimmers = useMemo(() => {
    return sortedSwimmers.filter((swimmer) => swimmer.is_mine);
  }, [sortedSwimmers]);

  const activeSlotNameByKey = useMemo(() => {
    const slotMap = {};
    (lineupSlots || []).forEach((slot) => {
      if (!slot?.key) return;
      slotMap[slot.key] = slot?.swimmer?.name || "";
    });
    return slotMap;
  }, [lineupSlots]);

  const suggestedTrades = useMemo(() => {
    if (myTeamSwimmers.length === 0) return [];

    const byPotential = (swimmer) => {
      const potential = getPotential(swimmer);
      const bonus =
        potential.label === "High" ? 3 : potential.label === "Medium" ? 1.5 : 0;
      return potential.avgDrop + bonus;
    };

    const myLowest = [...myTeamSwimmers]
      .sort((a, b) => byPotential(a) - byPotential(b))
      .slice(0, 3);

    const watchTargets = watchedSwimmers.filter(
      (swimmer) => swimmer.owned_by_other,
    );

    const topWatchTargets = [...watchTargets]
      .sort((a, b) => byPotential(b) - byPotential(a))
      .slice(0, 3);

    const ideas = [];

    topWatchTargets.forEach((target, idx) => {
      const offer = myLowest[idx % myLowest.length];
      if (!offer) return;
      ideas.push({
        id: `watch-${target.name}-${offer.name}`,
        type: "trade",
        toCoach: target.coach || "Coach",
        offer: offer.name,
        request: target.name,
        reason: `${target.name} has stronger upside while ${offer.name} is your lower-potential piece.`,
      });
    });

    return ideas;
  }, [myTeamSwimmers, watchedSwimmers]);

  const addPendingTrade = (trade) => {
    setPendingTrades((prev) => {
      if (prev.some((item) => item.id === trade.id)) return prev;
      const next = [...prev, { ...trade, status: "Pending approval" }];
      localStorage.setItem(PENDING_TRADES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const removePendingTrade = (tradeId) => {
    setPendingTrades((prev) => {
      const next = prev.filter((item) => item.id !== tradeId);
      localStorage.setItem(PENDING_TRADES_KEY, JSON.stringify(next));
      return next;
    });
  };

  if (loading) {
    return <p className="home-dashboard-loading">Loading home analytics…</p>;
  }

  if (error) {
    return (
      <div className="home-dashboard-error">
        <strong>⚠ Dashboard error</strong>
        {error}
      </div>
    );
  }

  return (
    <div className="home-dashboard-page">
      <h1 className="home-page-title home-dashboard-title">
        {coachName}&apos;s Dashboard
      </h1>

      <button
        type="button"
        className="home-score-box"
        onClick={() => setShowMeetBreakdown(true)}
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            flex: 1,
          }}
        >
          <span className="home-score-box-label">Coach Score</span>
          <span className="home-score-box-value">
            {Number(coachScoreTotal || 0).toFixed(2)}
          </span>
          <span className="home-score-box-hint">
            Click to view meet breakdown
          </span>
        </div>

        {mySwissRecord && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              alignItems: "flex-end",
              padding: "0.25rem 0.4rem 0.25rem 0.75rem",
              borderLeft: "1px solid rgba(0,245,212,0.25)",
            }}
            aria-label="Bracket standings"
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span
                style={{
                  fontSize: "0.62rem",
                  letterSpacing: "0.1em",
                  color: "#9fb7e3",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Wins
              </span>
              <span style={{ fontSize: "1.7rem", fontWeight: 800, color: "#00f5d4", lineHeight: 1 }}>
                {mySwissRecord.wins ?? 0}
              </span>
            </div>
            <span style={{ fontSize: "1.7rem", color: "#6884b7", lineHeight: 1, alignSelf: "flex-end" }}>
              /
            </span>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span
                style={{
                  fontSize: "0.62rem",
                  letterSpacing: "0.1em",
                  color: "#9fb7e3",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Losses
              </span>
              <span style={{ fontSize: "1.7rem", fontWeight: 800, color: "#ff93b0", lineHeight: 1 }}>
                {mySwissRecord.losses ?? 0}
              </span>
            </div>
          </div>
        )}
      </button>

      {myMatchup && (
        <div
          className="home-matchup-box"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/league/bracket")}
          onKeyDown={(e) => e.key === "Enter" && navigate("/league/bracket")}
          style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem" }}
        >
          <span className="home-matchup-label">
            Week {matchupWeek} Matchup
          </span>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "180px" }}>
              <MascotLogo
                imageDataUrl={mascotFor(coachName)}
                coachName={coachName}
                size={180}
                borderRadius="14px"
                style={{ marginBottom: "0.5rem" }}
              />
              <span style={{ fontSize: "1.5rem", fontWeight: 800, color: "#eaf3ff", letterSpacing: "0.01em" }}>
                {coachName}
              </span>
              {mySwissRecord && (
                <span style={{ fontSize: "1rem", color: "#8ea8d8", marginTop: "0.15rem" }}>
                  ({mySwissRecord.wins}-{mySwissRecord.losses})
                </span>
              )}
            </div>

            <span
              style={{
                fontSize: "1.9rem",
                fontWeight: 800,
                color: "#9fb7e3",
                letterSpacing: "0.04em",
                padding: "0 0.6rem",
              }}
            >
              vs
            </span>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "180px" }}>
              {myMatchup.isBye ? (
                <span style={{ fontSize: "1.4rem", fontWeight: 700, color: "#6884b7", fontStyle: "italic", padding: "1rem 0" }}>
                  BYE
                </span>
              ) : (
                <>
                  <MascotLogo
                    imageDataUrl={mascotFor(myMatchup.coach)}
                    coachName={myMatchup.coach}
                    size={180}
                    borderRadius="14px"
                    style={{ marginBottom: "0.5rem" }}
                  />
                  <span style={{ fontSize: "1.5rem", fontWeight: 800, color: "#cddcf8", letterSpacing: "0.01em" }}>
                    {myMatchup.coach}
                  </span>
                  {myMatchup.wins != null && (
                    <span style={{ fontSize: "1rem", color: "#8ea8d8", marginTop: "0.15rem" }}>
                      ({myMatchup.wins}-{myMatchup.losses})
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {!myMatchup.isBye && (
            <span className="home-matchup-hint">Tap to view all matchups</span>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.4rem",
          margin: "0.5rem 0 1rem 0",
        }}
      >
        <MascotLogo
          imageDataUrl={readMyMascot()}
          coachName={coachName}
          size={180}
          borderRadius="14px"
        />
        <div
          style={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: "#cddcf8",
            letterSpacing: "0.02em",
          }}
        >
          {coachName}
        </div>
      </div>

      <div className="home-top-layout">
        <section className="home-main-panel">
          <h2 className="home-section-title">My Team</h2>

          <div className="home-team-slots-grid">
            {TEAM_SLOT_ROWS.map((slotRow) => (
              <div key={slotRow.label} className="home-slot-group-card">
                <h3 className="home-slot-group-title">{slotRow.label}</h3>
                <div className="home-slot-names-grid">
                  <div
                    className={`home-slot-name-cell ${
                      activeSlotNameByKey[slotRow.girlKey]
                        ? "home-slot-name-cell-filled"
                        : ""
                    }`}
                  >
                    <div className="home-slot-name-top-row">
                      <span
                        className={`home-slot-name-label ${
                          activeSlotNameByKey[slotRow.girlKey]
                            ? "home-slot-name-label-filled"
                            : ""
                        }`}
                      >
                        Girls
                      </span>
                    </div>
                    <span className="home-slot-name-value">
                      {activeSlotNameByKey[slotRow.girlKey] || ""}
                    </span>
                  </div>
                  <div
                    className={`home-slot-name-cell ${
                      activeSlotNameByKey[slotRow.boyKey]
                        ? "home-slot-name-cell-filled"
                        : ""
                    }`}
                  >
                    <div className="home-slot-name-top-row">
                      <span
                        className={`home-slot-name-label ${
                          activeSlotNameByKey[slotRow.boyKey]
                            ? "home-slot-name-label-filled"
                            : ""
                        }`}
                      >
                        Boys
                      </span>
                    </div>
                    <span className="home-slot-name-value">
                      {activeSlotNameByKey[slotRow.boyKey] || ""}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <aside className="home-watch-panel">
          <h2 className="home-section-title">Swimmers to Watch</h2>

          {watchedSwimmers.length === 0 ? (
            <p>No watched swimmers yet. Add swimmers from Tracker.</p>
          ) : (
            <div className="home-watch-list">
              {watchedSwimmers.map((swimmer) => (
                <div key={`watch-${swimmer.name}`} className="home-watch-card">
                  <strong>{swimmer.name}</strong>
                  <div className="home-watch-meta">
                    Age: {swimmer.age ?? "—"}
                  </div>
                  <div className="home-watch-meta">
                    Points: {Number(swimmer.total_points || 0).toFixed(2)}
                  </div>
                  <div className="home-watch-times">
                    {renderTimesSummary(swimmer.best_times || {})}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      <section className="home-trade-panel">
        <h2 className="home-section-title">Trades</h2>

        <div className="home-trade-grid">
          <div className="home-trade-card">
            <h3 className="home-trade-card-title">Pending Trades</h3>
            {pendingTrades.length === 0 ? (
              <p className="home-trade-note">No pending trades yet.</p>
            ) : (
              <div className="home-trade-list">
                {pendingTrades.map((trade) => (
                  <div key={trade.id} className="home-trade-item">
                    <div className="home-trade-headline">
                      {`Offer ${trade.offer} for ${trade.request}`}
                    </div>
                    <div className="home-trade-meta">
                      {trade.toCoach ? `With ${trade.toCoach}` : ""}
                    </div>
                    <div className="home-trade-meta">{trade.status}</div>
                    <button
                      type="button"
                      className="home-remove-trade-btn"
                      onClick={() => removePendingTrade(trade.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="home-trade-card">
            <h3 className="home-trade-card-title">Suggested Trades</h3>
            {suggestedTrades.length === 0 ? (
              <p className="home-trade-note">
                No suggestions yet. Build your team/watch list to generate
                ideas.
              </p>
            ) : (
              <div className="home-trade-list">
                {suggestedTrades.map((trade) => (
                  <div key={trade.id} className="home-trade-item">
                    <div className="home-trade-headline">
                      {`Offer ${trade.offer} for ${trade.request}`}
                    </div>
                    <div className="home-trade-meta">
                      {trade.toCoach ? `Target coach: ${trade.toCoach}` : ""}
                    </div>
                    <div className="home-trade-reason">{trade.reason}</div>
                    <button
                      type="button"
                      className="home-queue-trade-btn"
                      onClick={() => addPendingTrade(trade)}
                    >
                      Add to Pending
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {showMeetBreakdown ? (
        <div
          className="home-score-modal-overlay"
          onClick={() => {
            setShowMeetBreakdown(false);
            setSelectedMeet(null);
          }}
        >
          <div
            className="home-score-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-score-modal-header">
              <h3 className="home-score-modal-title">Meet Score Breakdown</h3>
              <button
                type="button"
                className="home-score-modal-close"
                onClick={() => {
                  setShowMeetBreakdown(false);
                  setSelectedMeet(null);
                }}
              >
                ✕
              </button>
            </div>

            {meetBreakdown.length === 0 ? (
              <p className="home-score-modal-empty">No scored meets yet.</p>
            ) : (
              <div className="home-score-meet-list">
                {meetBreakdown.map((meet) => (
                  <button
                    type="button"
                    key={meet.meetId}
                    className="home-score-meet-row"
                    onClick={() => setSelectedMeet(meet)}
                  >
                    <span className="home-score-meet-name">
                      {meet.meetLabel}
                    </span>
                    <span className="home-score-meet-points">
                      {meet.totalPoints.toFixed(2)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showCaptainModal ? (
        <div className="home-captain-modal-overlay">
          <div className="home-captain-modal-card">
            <p>You need to choose captains before the next match.</p>
            <div className="home-captain-modal-buttons">
              <button
                type="button"
                className="home-captain-modal-btn"
                onClick={() => navigate("/team")}
              >
                Choose Captains
              </button>
              <button
                type="button"
                className="home-captain-modal-close"
                onClick={() => setShowCaptainModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedMeet ? (
        <div
          className="home-score-modal-overlay"
          onClick={() => setSelectedMeet(null)}
        >
          <div
            className="home-score-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-score-modal-header">
              <h3 className="home-score-modal-title">
                {selectedMeet.meetLabel}
              </h3>
              <button
                type="button"
                className="home-score-modal-close"
                onClick={() => setSelectedMeet(null)}
              >
                ✕
              </button>
            </div>

            {selectedMeet.swimmers?.length ? (
              <div className="home-score-swimmer-list">
                {selectedMeet.swimmers.map((swimmer) => (
                  <div
                    key={`${selectedMeet.meetId}-${swimmer.name}`}
                    className="home-score-swimmer-row"
                  >
                    <span className="home-score-swimmer-name">
                      {swimmer.name}
                    </span>
                    <span className="home-score-swimmer-points">
                      {swimmer.points.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="home-score-modal-empty">
                No active swimmers scored in this meet.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default HomeDashboard;
